import { Component, type ReactNode } from 'react';
import i18n from '@/i18n';

interface Props { children: ReactNode }
interface State { hasError: boolean }

// Catches render/runtime errors so a single failure never blacks out the whole
// app. Common culprit: browser extensions (Google Translate, Grammarly) mutating
// the DOM React owns, which throws "removeChild ... not a child of this node".
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: unknown) {
    console.error('App error caught by ErrorBoundary:', error);
  }

  handleReload = () => {
    this.setState({ hasError: false });
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background-100 px-6">
          <div className="text-center max-w-md">
            <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-primary-500/10">
              <i className="ri-refresh-line text-2xl text-primary-600"></i>
            </div>
            <h1 className="mt-4 font-heading text-xl font-bold text-foreground-900">
              {i18n.t('pub.error_title')}
            </h1>
            <p className="mt-2 text-sm text-foreground-500 font-body">
              {i18n.t('pub.error_message')}
            </p>
            <button
              onClick={this.handleReload}
              className="mt-5 px-6 py-3 rounded-full bg-primary-500 text-white text-sm tracking-[0.1em] uppercase font-label hover:bg-primary-600 transition-colors cursor-pointer"
            >
              {i18n.t('pub.error_reload')}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
